/**
 * Page Archives Financières — accessible pour les 3 types : bilan, compte-resultat, tresorerie.
 * Route : /archives/:type
 *
 * Fonctionnalités :
 *  - Liste des archives classées par date de génération décroissante
 *  - Bouton "Archiver maintenant" (admin uniquement) : capture l'état actuel
 *  - Pour chaque archive : Aperçu (nouvel onglet) + Téléchargement PDF + Suppression (admin)
 *  - Auto-classement par mois (groupement visuel)
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  ArrowLeft, FileArchive, Download, Eye, Plus, Trash2, Loader2,
  Scale, FileBarChart, Wallet, CalendarDays,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  'bilan': {
    label: 'Bilan Comptable',
    icon: Scale,
    color: 'indigo',
    headerBg: 'from-indigo-600 to-indigo-700',
    backRoute: '/bilan-investisseur',
    backRouteAdmin: '/entreprise?tab=finances&sub=bilan',
    kpiLabels: [
      { key: 'total_actif', label: 'Actif', color: 'text-blue-700' },
      { key: 'total_passif', label: 'Passif', color: 'text-amber-700' },
      { key: 'ecart', label: 'Écart', color: 'text-slate-700' },
    ],
  },
  'compte-resultat': {
    label: 'Compte de Résultat',
    icon: FileBarChart,
    color: 'emerald',
    headerBg: 'from-emerald-600 to-emerald-700',
    backRoute: '/compte-resultat-investisseur',
    backRouteAdmin: '/entreprise?tab=finances&sub=cr',
    kpiLabels: [
      { key: 'total_produits', label: 'Produits', color: 'text-emerald-700' },
      { key: 'total_charges', label: 'Charges', color: 'text-rose-700' },
      { key: 'resultat_net', label: 'Résultat net', color: 'text-slate-900 font-bold' },
    ],
  },
  'tresorerie': {
    label: 'Trésorerie',
    icon: Wallet,
    color: 'blue',
    headerBg: 'from-blue-600 to-blue-700',
    backRoute: '/tresorerie-investisseur',
    backRouteAdmin: '/tableau-tresorerie',
    kpiLabels: [
      { key: 'total_encaissements', label: 'Encaissements', color: 'text-emerald-700' },
      { key: 'total_decaissements', label: 'Décaissements', color: 'text-rose-700' },
      { key: 'solde', label: 'Solde', color: 'text-slate-900 font-bold' },
    ],
  },
};

const fmtFCFA = (v) => (Number(v) || 0).toLocaleString('fr-FR').replace(/,/g, ' ') + ' FCFA';

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso.slice(0, 16); }
};

const monthLabel = (iso) => {
  if (!iso) return 'Sans date';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  } catch { return 'Sans date'; }
};

const today = () => new Date().toISOString().split('T')[0];
const firstDayOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};

export default function ArchivesFinancieres({ user, onLogout }) {
  const { type } = useParams();
  const navigate = useNavigate();
  const meta = TYPE_META[type];
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Modal "Archiver maintenant"
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    date_debut: firstDayOfMonth(),
    date_fin: today(),
    libelle: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/archives-financieres`, { params: { type } });
      setItems(data.items || []);
    } catch (e) {
      console.error(e);
      toast.error('Erreur de chargement des archives');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!meta) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Groupement par mois (date_fin)
  const groupedByMonth = useMemo(() => {
    const groups = {};
    items.forEach((it) => {
      const key = monthLabel(it.date_fin || it.generated_at);
      if (!groups[key]) groups[key] = [];
      groups[key].push(it);
    });
    return Object.entries(groups);
  }, [items]);

  if (!meta) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-8 text-center text-slate-500">Type d'archive inconnu : {type}</div>
      </Layout>
    );
  }

  const handleCreate = async () => {
    if (!form.date_fin) {
      toast.error('La date de fin est obligatoire');
      return;
    }
    setCreating(true);
    try {
      const params = { type, date_fin: form.date_fin };
      if (form.date_debut) params.date_debut = form.date_debut;
      if (form.libelle) params.libelle = form.libelle;
      const { data } = await axios.post(`${API}/api/archives-financieres`, null, { params });
      toast.success(`Archive ${data.periode_label} créée`);
      setShowCreate(false);
      setForm({
        date_debut: firstDayOfMonth(),
        date_fin: today(),
        libelle: '',
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handlePreview = (a) => {
    const token = localStorage.getItem('token') || '';
    window.open(`${API}/api/archives-financieres/${a.id}/pdf?token=${encodeURIComponent(token)}`, '_blank');
  };

  const handleDownload = async (a) => {
    try {
      const { data } = await axios.get(`${API}/api/archives-financieres/${a.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${meta.label.replace(/ /g, '-')}-${a.date_fin || a.generated_at?.slice(0, 10)}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Téléchargement impossible');
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Supprimer définitivement l'archive « ${a.libelle || a.periode_label} » ?`)) return;
    try {
      await axios.delete(`${API}/api/archives-financieres/${a.id}`);
      toast.success('Archive supprimée');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Suppression impossible');
    }
  };

  const Icon = meta.icon;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-5" data-testid={`archives-page-${type}`}>
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate(isAdmin ? meta.backRouteAdmin : meta.backRoute)}
              className="gap-2"
              data-testid="archives-back-btn"
            >
              <ArrowLeft className="h-4 w-4" /> Retour
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-[#1C2A46] flex items-center gap-2">
                <FileArchive className={`h-7 w-7 text-${meta.color}-600`} />
                Rapport mensuel — {meta.label}
              </h1>
              <p className="text-slate-600 text-sm mt-1">
                {items.length} document{items.length > 1 ? 's' : ''} archivé{items.length > 1 ? 's' : ''}, classé{items.length > 1 ? 's' : ''} par date
              </p>
            </div>
          </div>
          {isAdmin && (
            <Button
              onClick={() => setShowCreate(true)}
              className={`bg-${meta.color}-600 hover:bg-${meta.color}-700 gap-2`}
              data-testid="archive-now-btn"
            >
              <Plus className="h-4 w-4" /> Archiver maintenant
            </Button>
          )}
        </div>

        {/* Bandeau d'info */}
        <Card className={`bg-gradient-to-r ${meta.headerBg} text-white border-0 shadow-md`}>
          <CardContent className="p-5 flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
              <Icon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold">Archivage des rapports {meta.label.toLowerCase()}</h3>
              <p className="text-sm opacity-90 mt-1">
                Chaque 1er du mois à 02h00, le rapport du mois précédent est automatiquement archivé. Vous pouvez également créer un instantané manuel à tout moment.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Liste */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className={`h-7 w-7 animate-spin text-${meta.color}-600`} />
          </div>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="text-center py-16 text-slate-500">
              <FileArchive className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="font-semibold text-slate-700 mb-1">Aucune archive pour le moment</p>
              <p className="text-sm">Les archives s'accumuleront automatiquement chaque début de mois.</p>
              {isAdmin && <p className="text-xs mt-3">Vous pouvez créer un instantané manuel via le bouton « Archiver maintenant ».</p>}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {groupedByMonth.map(([month, group]) => (
              <div key={month}>
                <div className="flex items-center gap-2 mb-2 text-slate-600 text-sm font-semibold uppercase tracking-wide">
                  <CalendarDays className="h-4 w-4" />
                  <span className="capitalize">{month}</span>
                  <span className="text-slate-400 normal-case">· {group.length} document{group.length > 1 ? 's' : ''}</span>
                </div>
                <div className="space-y-2">
                  {group.map((a) => (
                    <ArchiveRow
                      key={a.id}
                      archive={a}
                      meta={meta}
                      isAdmin={isAdmin}
                      onPreview={() => handlePreview(a)}
                      onDownload={() => handleDownload(a)}
                      onDelete={() => handleDelete(a)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Archiver maintenant */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Archiver le {meta.label.toLowerCase()}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Date de début</Label>
              <Input
                type="date"
                value={form.date_debut}
                onChange={(e) => setForm({ ...form, date_debut: e.target.value })}
                data-testid="archive-date-debut"
              />
            </div>
            <div>
              <Label className="text-xs">Date de fin</Label>
              <Input
                type="date"
                value={form.date_fin}
                onChange={(e) => setForm({ ...form, date_fin: e.target.value })}
                data-testid="archive-date-fin"
              />
            </div>
            <div>
              <Label className="text-xs">Libellé (optionnel)</Label>
              <Input
                placeholder={type === 'bilan' ? 'Ex: Bilan fin d\'exercice 2026' : 'Ex: Trimestre 1 2026'}
                value={form.libelle}
                onChange={(e) => setForm({ ...form, libelle: e.target.value })}
                data-testid="archive-libelle"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} disabled={creating}>
              Annuler
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating}
              className={`bg-${meta.color}-600 hover:bg-${meta.color}-700 gap-2`}
              data-testid="archive-create-btn"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileArchive className="h-4 w-4" />}
              Archiver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function ArchiveRow({ archive, meta, isAdmin, onPreview, onDownload, onDelete }) {
  const kpis = archive.kpis || {};
  const autoTag = archive.auto;

  return (
    <Card className="hover:shadow-md transition-all" data-testid={`archive-row-${archive.id}`}>
      <CardContent className="p-4">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          {/* Date + libellé */}
          <div className="flex items-center gap-3 flex-shrink-0 min-w-0 lg:w-72">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-${meta.color}-500 to-${meta.color}-700 flex flex-col items-center justify-center text-white flex-shrink-0`}>
              <span className="text-[9px] uppercase opacity-80">{(archive.date_fin || archive.generated_at || '').slice(0, 7).replace('-', '/')}</span>
              <span className="text-base font-bold leading-none">
                {(archive.date_fin || archive.generated_at || '').slice(8, 10) || '—'}
              </span>
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-slate-900 truncate">
                {archive.libelle || archive.periode_label}
              </p>
              <p className="text-xs text-slate-500 truncate">
                {archive.periode_label}
              </p>
              <p className="text-[11px] text-slate-400 truncate flex items-center gap-1.5">
                Généré le {fmtDateTime(archive.generated_at)}
                {autoTag && <Badge variant="outline" className="h-4 text-[9px] px-1 py-0">AUTO</Badge>}
              </p>
            </div>
          </div>

          {/* KPIs */}
          <div className="flex flex-1 gap-3 flex-wrap">
            {meta.kpiLabels.map((kp) => (
              <div key={kp.key} className="flex-1 min-w-[120px]">
                <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{kp.label}</p>
                <p className={`text-sm mt-0.5 ${kp.color}`}>{fmtFCFA(kpis[kp.key])}</p>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="outline" size="sm"
              onClick={onPreview}
              className="gap-1.5"
              data-testid={`preview-archive-${archive.id}`}
            >
              <Eye className="h-4 w-4" /> Aperçu
            </Button>
            <Button
              size="sm"
              onClick={onDownload}
              className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"
              data-testid={`download-archive-${archive.id}`}
            >
              <Download className="h-4 w-4" /> PDF
            </Button>
            {isAdmin && (
              <Button
                variant="ghost" size="sm"
                onClick={onDelete}
                className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                data-testid={`delete-archive-${archive.id}`}
                title="Supprimer"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
